import { delay, UnreachableError } from 'base/helpers';
import { DEFAULT_LOCALE, Locale } from 'base/ui/locales';
import { PresenceDecorations } from 'blocks/sync/presence_decorations';
import { computed, makeObservable, observable, reaction } from 'mobx';
import { computedFn } from 'mobx-utils';
import { CanvasViewport, EnsureVisibleOpts } from '../canvas';
import { EditorMode } from '../editor_mode';
import { ElementInstance, Project, Section } from '../model';
import { isChildInclusive, unrefSection } from '../model_helpers';
import { ProjectSession } from '../session/session';
import { SelectionFactory } from './factory';
import { SELECTABLE_ATTACH_FRAME_ID_ATTR } from './measurer';
import { idForSelectionState, SelectionState, statesEqual } from './states';

const isValidState = computedFn((state: SelectionState, project: Project) => {
  // validate space
  if (state.space == null) return true;
  const spaceInProject =
    state.space === 'home' || project.spaces.includes(state.space);
  if (!spaceInProject) return false;

  // validate page
  if (state.page == null) return true;
  const pageInSpace = isChildInclusive(
    project,
    state.space === 'home' ? project.homePage : state.space,
    state.page,
  );
  if (!pageInSpace) return false;

  // validate section
  if (state.section == null) return true;
  const sectionInPage = state.page.sections.includes(state.section);
  if (!sectionInPage) return false;

  // validate element
  if (state.element == null) return true;
  const sv = unrefSection(state.section, project);
  if (sv == null || !isElementInSection(sv, state.element)) return false;

  return true;
});

function isElementInSection(
  section: Section,
  element: ElementInstance,
): boolean {
  const root = section.element.current;
  if (root == null) return false;

  function visit(el: ElementInstance): boolean {
    if (el === element) return true;
    for (const slot of el.slotOverrides.values()) {
      for (const item of slot.items) {
        if (visit(item)) return true;
      }
    }
    return false;
  }

  return visit(root);
}

const EMPTY_SELECTION: SelectionState = Object.freeze({});

export interface SelectionStore {
  readonly project: Project;
  readonly canSelect: boolean;
  readonly factory: SelectionFactory;
  isValidState(state: SelectionState): boolean;
  get(): SelectionState;
  getId(): string | undefined;
  set(state: SelectionState, options?: SetSelectionOptions): void;
  readonly decorations: PresenceDecorations;
}

type SetSelectionOptions = {
  disableEnsureVisible?: boolean;
} & EnsureVisibleOpts;

export class SelectionStoreImpl implements SelectionStore {
  readonly factory = new SelectionFactory(this.project);

  // does not include the initial state
  private readonly history = observable.array<SelectionState>([], {
    deep: false,
  });

  constructor(
    readonly project: Project,
    private readonly getEditorMode: () => EditorMode,
    private readonly viewport: Pick<CanvasViewport, 'ensureVisible'>,
    private readonly session: Pick<ProjectSession, 'canViewWireframe'>,
    readonly decorations: PresenceDecorations,
    readonly canSelect: boolean,
  ) {
    makeObservable(this);
    this.history.push();
    reaction(
      () => this.getId(),
      (selId) => this.decorations.setSelection(selId),
    );
  }

  get() {
    return this.getReturnValue;
  }

  @computed
  private get getReturnValue() {
    const state = this.getFull();
    const mode = this.getEditorMode();
    switch (mode) {
      case EditorMode.Sitemap:
        return simplifySelectionForSitemap(state);
      case EditorMode.Wireframe:
      case EditorMode.Design:
        return simplifySelectionForWireframe(state, this.session);
      case EditorMode.Styleguide:
        // Style guide doesn't have selection state anyway
        return state;
      default:
        throw new UnreachableError(mode);
    }
  }

  getId() {
    const v = this.getReturnValue;
    return v ? idForSelectionState(v) : undefined;
  }

  private getFull() {
    for (let i = this.history.length - 1; i >= 0; i--) {
      const state = this.history[i];
      if (this.isValidState(state)) return state;
    }
    return EMPTY_SELECTION;
  }

  readonly isValidState = computedFn((state: SelectionState) =>
    isValidState(state, this.project),
  );

  set(state: SelectionState, opts?: SetSelectionOptions) {
    if (!isValidState(state, this.project)) {
      console.error(
        'Tried to set selection to a state that is ALREADY invalid',
        state,
      );
    }
    this.history.push(state);

    if (opts?.disableEnsureVisible) return;
    // On chrome, focusin seems to fire 1ms before click.
    // If we start the animation at focusin, the click might end up dispatching
    // against a different element.
    delay(1).then(() => {
      if (!statesEqual(state, this.get())) return;
      const el = document.querySelector(
        `[${SELECTABLE_ATTACH_FRAME_ID_ATTR}="${idForSelectionState(state)}"]`,
      );
      if (el instanceof HTMLElement) {
        if (!el.contains(document.activeElement)) {
          // Should this be before the delay?
          el.focus();
        }
        this.viewport.ensureVisible(el, opts);
      }
    });
  }
}

/** Do not allow node selections, since wireframes are not displayed */
function simplifySelectionForSitemap(s: SelectionState): SelectionState {
  if (s.section == null) return s;
  return { space: s.space, page: s.page, section: s.section };
}

/** Do not allow section-or-deeper selections if a page can not be viewed */
function simplifySelectionForWireframe(
  s: SelectionState,
  session: Pick<ProjectSession, 'canViewWireframe'>,
) {
  if (s.page == null) return s;
  if (session.canViewWireframe(s.page)) return s;
  return { space: s.space, page: s.page };
}

export function selectionLocale(sel: SelectionState, project: Project): Locale {
  return (
    (sel.space && sel.space !== 'home' && sel.space.localeOverride) ||
    project.locale ||
    DEFAULT_LOCALE
  );
}
